/**
 * Service for managing data storage in localStorage and sessionStorage
 */
export class DataStorageService {
  /**
   * Set up a listener for storage changes (both cross-tab and same-tab)
   * @param callback Function to call when a storage event occurs
   * @returns A cleanup function to remove the listener
   */
  static setupStorageListener(callback: (key: string) => void): () => void {
    // Handler for cross-tab storage events
    const storageHandler = (event: StorageEvent) => {
      if (event.key && event.newValue) {
        callback(event.key);
      }
    };
    
    // Handler for same-tab custom events
    const customHandler = (event: CustomEvent) => {
      if (event.detail && event.detail.key) {
        callback(event.detail.key);
      }
    };
    
    window.addEventListener('storage', storageHandler);
    window.addEventListener('dataStorageUpdate', customHandler as EventListener);
    
    // Return a cleanup function
    return () => {
      window.removeEventListener('storage', storageHandler);
      window.removeEventListener('dataStorageUpdate', customHandler as EventListener);
    };
  }

  /**
   * Store data in localStorage (persistent) or sessionStorage (session-only)
   * @param key Storage key
   * @param value Value to store
   * @param persistent If true, store in localStorage, otherwise sessionStorage
   */
  static storeData(key: string, value: unknown, persistent: boolean = false): void {
    try {
      // Convert to JSON string if it's not already a string
      const valueToStore = typeof value === 'string' ? value : JSON.stringify(value);
      
      // Store in the appropriate storage
      if (persistent) {
        localStorage.setItem(key, valueToStore);
      } else {
        sessionStorage.setItem(key, valueToStore);
      }

      // Dispatch custom event for same-tab communication
      window.dispatchEvent(new CustomEvent('dataStorageUpdate', {
        detail: { key, value: valueToStore, persistent }
      }));
    } catch (error) {
      console.error(`Error storing data for key ${key}:`, error);
    }
  }

  /**
   * Get data from storage
   * @param key Storage key
   * @param persistent If true, check localStorage, otherwise sessionStorage
   * @returns The stored value, or null if not found
   */
  static getData<T>(key: string, persistent: boolean = false): T | null {
    try {
      // Get from appropriate storage
      const item = persistent ? localStorage.getItem(key) : sessionStorage.getItem(key);

      if (!item) {
        return null;
      }

      // Try to parse JSON, but if it fails, return the raw string
      try {
        return JSON.parse(item) as T;
      } catch {
        // If JSON parsing fails, return as string (cast to T)
        return item as T;
      }
    } catch (error) {
      console.error(`Error getting data for key ${key}:`, error);
      return null;
    }
  }

  /**
   * Remove data from storage
   * @param key Storage key
   * @param persistent If true, remove from localStorage, otherwise sessionStorage
   */
  static removeData(key: string, persistent: boolean = false): void {
    try {
      if (persistent) {
        localStorage.removeItem(key);
      } else {
        sessionStorage.removeItem(key);
      }
    } catch (error) {
      console.error(`Error removing data for key ${key}:`, error);
    }
  }

  /**
   * Check if a key exists in storage
   * @param key Storage key
   * @param persistent If true, check localStorage, otherwise sessionStorage
   * @returns True if the key exists, false otherwise
   */
  static hasData(key: string, persistent: boolean = false): boolean {
    const storage = persistent ? localStorage : sessionStorage;
    return storage.getItem(key) !== null;
  }

  /**
   * Get all keys in storage
   * @param persistent If true, get keys from localStorage, otherwise sessionStorage
   * @returns Array of keys
   */
  static getAllKeys(persistent: boolean = false): string[] {
    const storage = persistent ? localStorage : sessionStorage;
    return Object.keys(storage);
  }

  /**
   * Broadcast a message to all browser tabs/windows using BroadcastChannel
   * @param type The type of message
   * @param data Additional data to send
   */
  static broadcastMessage(type: string, data?: unknown): void {
    try {
      const channel = new BroadcastChannel('booking-updates');
      channel.postMessage({ type, data, timestamp: Date.now() });
      channel.close();
    } catch (error) {
      console.error('Error broadcasting message:', error);
    }
  }

  /**
   * Notify all tabs that a booking has changed (convenience method)
   */
  static notifyBookingChanged(): void {
    // Also set the localStorage trigger for same-tab updates
    this.storeData('booking-refresh-trigger', Date.now().toString());
    // Broadcast to other tabs
    this.broadcastMessage('booking-changed');
  }

  /**
   * Send push notification to other devices (placeholder for FCM implementation)
   * This would be called from the backend when bookings change
   */
  static sendCrossDeviceNotification(type: string, data?: unknown): void {
    // TODO: Implement Firebase Cloud Messaging
    // This would be called from Cloud Functions when bookings are modified
    console.log('Cross-device notification:', type, data);
  }
}

